import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { AlarmClock, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const pub = axios.create({ withCredentials: false });
const fmt = (iso) => new Date(iso).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

export default function SnoozeTask() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [pick, setPick] = useState(params.get("o") || "1d");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    pub.get(`${API_BASE}/tasks/snooze/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [token]);

  const go = async () => {
    setBusy(true);
    try { setDone((await pub.post(`${API_BASE}/tasks/snooze/${token}`, { option: pick })).data); }
    catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[var(--bg)]" data-testid="snooze-page">
      <div className="w-full max-w-md card p-6 space-y-4">
        <div className="overline inline-flex items-center gap-1.5"><AlarmClock size={12} /> Snooze task</div>
        {err ? (
          <div data-testid="snooze-error"><AlertTriangle className="text-[var(--accent)] mb-2" /><p className="text-[14px]">{err}</p>
            <Link to="/tasks" className="btn-secondary mt-4 inline-flex">Open my tasks</Link></div>
        ) : !info ? <Loader2 className="animate-spin text-[var(--ink-3)]" /> : done ? (
          <div data-testid="snooze-done">
            <CheckCircle2 className="text-[var(--moss)] mb-2" />
            <h1 className="font-heading text-2xl">Snoozed</h1>
            <p className="text-[14px] text-[var(--ink-2)] mt-1">“{info.title}” is now due {fmt(done.due_at)}.</p>
            <Link to={`/tasks?task_id=${info.task_id}`} className="btn-secondary mt-4 inline-flex" data-testid="snooze-open-task">Open task</Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl text-[var(--ink)]" data-testid="snooze-title">{info.title}</h1>
            {info.due_at && <p className="text-[12px] text-[var(--ink-3)]">Currently due {fmt(info.due_at)}</p>}
            <div className="space-y-2">
              {info.options.map((o) => (
                <label key={o.key} className={`flex items-center justify-between p-3 rounded-md border cursor-pointer transition-colors ${pick === o.key ? "border-[var(--ink)] bg-[var(--subtle)]" : "border-[var(--border)]"}`}
                       data-testid={`snooze-option-${o.key}`}>
                  <span className="inline-flex items-center gap-2 text-[14px]">
                    <input type="radio" name="snooze" checked={pick === o.key} onChange={() => setPick(o.key)} /> {o.label}
                  </span>
                  <span className="text-[12px] text-[var(--ink-3)]">{fmt(o.until)}</span>
                </label>
              ))}
            </div>
            <button className="btn-primary w-full justify-center" onClick={go} disabled={busy || info.status === "done"} data-testid="snooze-confirm">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <AlarmClock size={14} />} {info.status === "done" ? "Task already done" : "Snooze"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
